/**
 * Fork Yeah order-ahead helpers:
 * - bind day picker (save Pickup day, clear stale, gate CTA)
 * - auto-select preorder variant matching cart Pickup day
 */
(function () {
  const ATTR_DEFAULT = 'Pickup day';

  function birdAttributesForDay(day) {
    if (window.FYBirdPickup && typeof window.FYBirdPickup.attributesForDay === 'function') {
      return window.FYBirdPickup.attributesForDay(day) || {};
    }
    return {};
  }

  function updateCartAttributes(attributes) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    }).then((r) => {
      if (!r.ok) throw new Error('cart');
      return r.json();
    });
  }

  function setAheadCtaEnabled(root, enabled) {
    root.querySelectorAll('[data-ahead-cta]').forEach((cta) => {
      if (enabled) cta.removeAttribute('aria-disabled');
      else cta.setAttribute('aria-disabled', 'true');
    });
  }

  function bindPicker(root, options = {}) {
    if (!root || root.dataset.fyBound === '1') return;
    root.dataset.fyBound = '1';

    const attr = root.dataset.pickupAttribute || ATTR_DEFAULT;
    const confirm = root.dataset.confirm || 'Pickup set for';
    const prompt =
      root.dataset.prompt ||
      root.querySelector('.fy-om__status')?.dataset.default ||
      'Pick a pickup day above';
    const status = root.querySelector('.fy-om__status');
    const radios = [...root.querySelectorAll('.fy-day__radio[data-open-day]')];
    const clearStale = options.clearStale === true || root.dataset.clearStale === 'true';

    const syncCta = () => {
      const selected = radios.find((r) => r.checked);
      setAheadCtaEnabled(root, Boolean(selected));
    };

    if (clearStale) {
      updateCartAttributes({ [attr]: '' }).catch(() => {});
      if (status) status.textContent = prompt;
      radios.forEach((r) => {
        r.checked = false;
      });
    }

    radios.forEach((input) => {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        const day = input.value;
        if (status) status.textContent = 'Saving…';
        updateCartAttributes({ [attr]: day, ...birdAttributesForDay(day) })
          .then(() => {
            if (status) status.textContent = `${confirm} ${day}`;
            syncCta();
            document.dispatchEvent(
              new CustomEvent('fy:pickup-day', { detail: { day, attribute: attr } })
            );
          })
          .catch(() => {
            if (status) status.textContent = 'Could not save your day — please try again.';
          });
      });
    });

    syncCta();
  }

  async function fetchPickupDay(attributeName) {
    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      return cart?.attributes?.[attributeName] || '';
    } catch {
      return '';
    }
  }

  function productHasPreorderTag(productJson) {
    const tags = productJson?.tags;
    if (Array.isArray(tags)) return tags.includes('preorder');
    if (typeof tags === 'string') return tags.split(/,\s*/).includes('preorder');
    return false;
  }

  function findVariantIdForDay(productJson, dayLabel) {
    if (!productJson?.variants || !dayLabel) return null;
    const match = productJson.variants.find((v) => {
      if (!v.available) return false;
      const opts = v.options || [];
      const title = v.title || '';
      return opts.some((o) => String(o) === String(dayLabel)) || title === dayLabel;
    });
    return match?.id || null;
  }

  async function autoSelectPickupVariant(options = {}) {
    const attributeName = options.attributeName || ATTR_DEFAULT;
    const productJson = options.productJson || window.FYProductJson;
    if (!productJson || !productHasPreorderTag(productJson)) return;

    const day = await fetchPickupDay(attributeName);
    const note = document.querySelector('[data-fy-pickup-variant-note]');
    if (!day) {
      if (note) {
        note.hidden = false;
        note.textContent = 'Pick a pickup day above.';
      }
      setReserveAtcEnabled(false, 'Pick a day');
      return;
    }

    const variantId = findVariantIdForDay(productJson, day);
    const idInput = document.querySelector(
      'product-form-component [name="id"], form[data-type="add-to-cart-form"] [name="id"]'
    );

    if (!variantId) {
      if (note) {
        note.hidden = false;
        note.textContent =
          'That pickup day isn’t available for this item — pick another day above.';
      }
      setReserveAtcEnabled(false, 'Pick a day');
      return;
    }

    if (idInput) {
      idInput.value = String(variantId);
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    document.querySelectorAll(`[data-variant-id="${variantId}"], [value="${variantId}"]`).forEach((el) => {
      if (el instanceof HTMLInputElement) {
        el.checked = true;
        el.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    if (note) {
      note.hidden = false;
      note.textContent = `Reserving for ${day}.`;
    }
    setReserveAtcEnabled(true, 'Reserve');
  }

  function mealBuilderAllowsReserve() {
    const meal = document.querySelector('meal-builder');
    if (!meal) return true;
    return meal.hasAttribute('data-valid');
  }

  function setReserveAtcEnabled(enabled, label) {
    const allow = Boolean(enabled) && mealBuilderAllowsReserve();
    document.querySelectorAll('product-form-component[data-fy-need-day]').forEach((form) => {
      form.querySelectorAll('button[name="add"], .fy-reserve-cta, .add-to-cart-button').forEach((btn) => {
        if (!(btn instanceof HTMLButtonElement)) return;
        btn.disabled = !allow;
        if (allow) {
          delete btn.dataset.fyMealGated;
        } else if (enabled) {
          btn.dataset.fyMealGated = 'true';
        }
        if (label == null) return;

        const labelEl = btn.querySelector('[data-fy-reserve-label]');
        if (labelEl) {
          labelEl.textContent = label;
          return;
        }

        // Legacy ATC markup fallback — only touch dedicated text wrappers, never wipe icons.
        const textTarget =
          btn.querySelector('.add-to-cart-text__content > span') ||
          btn.querySelector('.add-to-cart-text__content') ||
          btn.querySelector('.add-to-cart-text [data-add-to-cart-text]');
        if (textTarget) textTarget.textContent = label;
      });
    });
  }

  /**
   * Product-page picker: variant mode sets variant id from data-variant-id;
   * schedule mode keeps the current variant and only saves Pickup day.
   */
  function bindProductPicker(root) {
    if (!root || root.dataset.fyBound === '1') return;
    root.dataset.fyBound = '1';

    const attr = root.dataset.pickupAttribute || ATTR_DEFAULT;
    const confirm = root.dataset.confirm || 'Reserving for';
    const status = root.querySelector('[data-fy-pdp-status]');
    const radios = [...root.querySelectorAll('.fy-pdp-day__radio')];
    const scheduleMode = root.dataset.fyScheduleMode === 'true';

    const applyVariant = (variantId) => {
      if (scheduleMode || !variantId) return;
      const idInput = document.querySelector(
        'product-form-component [name="id"], form[data-type="add-to-cart-form"] [name="id"]'
      );
      if (idInput) {
        idInput.value = String(variantId);
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };

    const syncPickupProperty = (day) => {
      document.querySelectorAll('[data-fy-pickup-property], input[name="properties[Pickup day]"]').forEach((el) => {
        if (el instanceof HTMLInputElement) el.value = day || '';
      });
    };

    const syncAtc = () => {
      const selected = radios.find((r) => r.checked);
      if (selected) {
        setReserveAtcEnabled(true, 'Reserve');
      } else {
        setReserveAtcEnabled(false, 'Pick a day');
      }
    };

    const preselected = radios.find((r) => r.checked);
    if (preselected) {
      applyVariant(preselected.dataset.variantId);
      syncPickupProperty(preselected.value);
    }
    syncAtc();

    radios.forEach((input) => {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        const day = input.value;
        applyVariant(input.dataset.variantId);
        syncPickupProperty(day);
        setReserveAtcEnabled(true, 'Reserve');
        // Variant morph can replace the button from Liquid (still gated) — re-enable after.
        requestAnimationFrame(() => setTimeout(() => setReserveAtcEnabled(true, 'Reserve'), 80));
        if (status) status.textContent = 'Saving…';
        updateCartAttributes({ [attr]: day, ...birdAttributesForDay(day) })
          .then(() => {
            if (status) status.textContent = `${confirm} ${day}`;
            setReserveAtcEnabled(true, 'Reserve');
            document.dispatchEvent(
              new CustomEvent('fy:pickup-day', { detail: { day, attribute: attr } })
            );
          })
          .catch(() => {
            if (status) status.textContent = 'Could not save your day — please try again.';
          });
      });
    });
  }

  function initAll() {
    document.querySelectorAll('[data-fy-order-ahead]').forEach((root) => bindPicker(root));
    document.querySelectorAll('[data-fy-pdp-pickup]').forEach((root) => bindProductPicker(root));
    // The product-page picker owns variant selection when present.
    if (window.FYProductJson && !document.querySelector('[data-fy-pdp-pickup]')) {
      autoSelectPickupVariant({ productJson: window.FYProductJson });
    }
  }

  window.FYOrderAhead = {
    bindPicker,
    bindProductPicker,
    autoSelectPickupVariant,
    fetchPickupDay,
    updateCartAttributes,
  };

  document.addEventListener('fy:pickup-day', () => {
    if (document.querySelector('[data-fy-pdp-pickup]')) {
      setReserveAtcEnabled(true, 'Reserve');
      return;
    }
    if (window.FYProductJson) {
      autoSelectPickupVariant({ productJson: window.FYProductJson });
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
